import { createSiteLineIdentityClient, SiteLineIdentityError, type SiteLineSession } from './managed-identity-client';

export const MANAGED_IDENTITY_ENABLED = import.meta.env.VITE_IDENTITY_GCP_ENABLED === 'true';
export type SessionState = { status: 'loading' | 'authenticated' | 'anonymous' | 'unavailable' | 'signing-out' | 'logout-failed'; session: SiteLineSession | null };
type Api = Pick<ReturnType<typeof createSiteLineIdentityClient>, 'session' | 'logout'>;
type Options = { api: Api; clearPrivateState(): void; broadcastLogout(confirmed: boolean): void; publishSession?(session: SiteLineSession | null): void; now?: () => number };

/** One in-memory lifecycle. Cookies remain HttpOnly; no credentials in storage or messages. */
export function createManagedSession(options: Options) {
  let state: SessionState = { status: 'loading', session: null };
  let generation = 0, logoutRequested = false, logoutConfirmed = false, logoutCsrf: string | undefined;
  let refreshFlight: Promise<void> | null = null, logoutFlight: Promise<void> | null = null;
  let refreshController: AbortController | null = null;
  const listeners = new Set<() => void>();
  const now = options.now || Date.now;
  const scope = (session: SiteLineSession | null) => session ? JSON.stringify([session.uid, session.principalUid, session.tenantId, session.branchId, session.orgRole, session.role, session.tier,
    session.profile?.email, session.profile?.emailVerified]) : '';
  function publish(next: SessionState) {
    if (scope(state.session) !== scope(next.session)) options.clearPrivateState();
    options.publishSession?.(next.session);
    state = next; listeners.forEach(listener => listener());
  }
  async function refresh() {
    if (logoutRequested) return;
    if (refreshFlight) return refreshFlight;
    const attempt = generation;
    const controller = new AbortController(); refreshController = controller;
    const task = (async () => {
      try {
        const session = await options.api.session(controller.signal);
        if (generation !== attempt || logoutRequested) return;
        if (session && session.expiresAt <= now()) throw new SiteLineIdentityError('invalid_identity_response');
        publish({ status: session ? 'authenticated' : 'anonymous', session });
      } catch {
        if (generation === attempt && !logoutRequested) publish({ status: 'unavailable', session: null });
      }
    })();
    refreshFlight = task;
    try { await task; } finally { if (refreshFlight === task) refreshFlight = null; if (refreshController === controller) refreshController = null; }
  }
  async function logout() {
    if (logoutConfirmed) return;
    if (logoutFlight) return logoutFlight;
    logoutCsrf ??= state.session?.csrf;
    logoutRequested = true; generation++; refreshController?.abort(); refreshFlight = null;
    publish({ status: 'signing-out', session: null });
    options.clearPrivateState(); options.broadcastLogout(false);
    const task = (async () => {
      try {
        // The BFF client can recover logout CSRF without resolving a profile
        // or admitting a product session. Never restore UI during revocation.
        await options.api.logout(logoutCsrf || '');
        logoutConfirmed = true;
        logoutCsrf = undefined;
        publish({ status: 'anonymous', session: null });
        options.broadcastLogout(true);
      } catch {
        if (!logoutConfirmed) publish({ status: 'logout-failed', session: null });
      }
    })();
    logoutFlight = task;
    try { await task; } finally { if (logoutFlight === task) logoutFlight = null; }
  }
  return {
    getSnapshot: () => state,
    subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; },
    refresh, logout,
    invalidate() { generation++; refreshController?.abort(); refreshFlight = null; if (!logoutRequested) publish({ status: 'unavailable', session: null }); },
    suspend() { generation++; refreshController?.abort(); refreshFlight = null; },
    remoteLogout(confirmed = false) {
      logoutCsrf ??= state.session?.csrf;
      generation++; refreshController?.abort(); refreshFlight = null; logoutRequested = true;
      if (confirmed) { logoutCsrf = undefined; logoutConfirmed = true; }
      publish({ status: logoutConfirmed ? 'anonymous' : 'logout-failed', session: null }); options.clearPrivateState();
    },
    expire() {
      if (state.session && state.session.expiresAt <= now()) { generation++; refreshController?.abort(); refreshFlight = null; publish({ status: 'anonymous', session: null }); }
    },
  };
}

